import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, NativeEventEmitter,
} from 'react-native';
import Colors from '../../../../constants/Colors';
import ResolvedIcon from '../../../../components/16pxIcons/ResolvedIcon';
import ProceedIcon from '../../../../components/16pxIcons/ProceedIcon';
import isMobile from '../../../../components/IsMobile';
import { logEvent } from '../../../../components/LogEvent';
import updateTicketStatus from '../../../../requests/Tickets/UpdateTicketStatus';
import PressableStyled from '../../../../components/PressableStyled';
import { Ticket, TicketStatus } from '../../../../constants/Types';
import { TICKET_STATUS_UPDATE_FAILURE } from '../../../../constants/AlertMessages';

interface TicketStatusButtonsRowProps {
  selectedTicket: Ticket,
  updateSelectedTicketInList,
  showSpinner: boolean,
  setShowSpinner: Dispatch<SetStateAction<boolean>>,
  showAlert,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setClosureModalVisible: Dispatch<SetStateAction<boolean>>,
}

const TicketStatusButtonsRow = ({
  selectedTicket, setLoading, setShowSpinner, showAlert, showSpinner, setClosureModalVisible,
  updateSelectedTicketInList,
}: TicketStatusButtonsRowProps) => {
  const emitter: NativeEventEmitter | null = isMobile ? null : new NativeEventEmitter();

  const ticketUpdateError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? TICKET_STATUS_UPDATE_FAILURE,
  );

  const handleTicketUpdate = (updateOperation: Promise<any>) => {
    setShowSpinner(true);
    updateOperation.then((response) => {
      if (response && !response.isError) {
        updateSelectedTicketInList(response);
      } else ticketUpdateError(response?.errorText);
      setLoading(true);
      emitter?.emit('event.updatenotifications', {});
    }).catch(() => {
      ticketUpdateError('Unable to update ticket status at the moment');
    }).finally(() => setShowSpinner(false));
  };

  const startTicket: () => void = () => {
    logEvent('Tickets', 'start_resolving');
    setShowSpinner(true);
    handleTicketUpdate(updateTicketStatus(selectedTicket.id, TicketStatus.INPROGRESS));
  };

  const reopenTicket: () => void = () => {
    logEvent('Tickets', 'reopen');
    setShowSpinner(true);
    handleTicketUpdate(updateTicketStatus(selectedTicket.id, TicketStatus.INPROGRESS));
  };

  const resolveTicket: () => void = () => setClosureModalVisible(true);

  return <>
    {selectedTicket.status === TicketStatus.REPORTED && <PressableStyled
      style={[styles.statusButton, styles.startButton]}
      onPress={startTicket}
      disabled={showSpinner}
      testID='ticket-start-btn'
    >
      <ProceedIcon />
      <Text style={[styles.resolveText, { color: '#fff' }]}>{'Start'}</Text>
    </PressableStyled>}
    {selectedTicket.status === TicketStatus.INPROGRESS && <PressableStyled
      onPress={resolveTicket}
      disabled={showSpinner}
      style={[styles.statusButton, styles.resolveButton]}
      testID='ticket-resolve-btn'
    >
      <ResolvedIcon color={styles.resolveButton.color} />
      <Text style={[styles.resolveText, { color: styles.resolveButton.color }]}>
        {'Resolve'}
      </Text>
    </PressableStyled>}
    {selectedTicket.status === TicketStatus.COMPLETED && (
      <View style={styles.completedRow}>
        <View style={[styles.statusButton, styles.completedButton]}>
          <ResolvedIcon />
          <Text style={styles.resolveText}>{'Resolved'}</Text>
        </View>
        <PressableStyled
          onPress={reopenTicket}
          style={[styles.statusButton, styles.reopenButton]}
          testID='ticket-reopen-btn'
          disabled={showSpinner}
        >
          <ProceedIcon />
          <Text style={[styles.resolveText, { color: '#fff' }]}>{'Reopen'}</Text>
        </PressableStyled>
      </View>
    )}
  </>;
};

const styles = StyleSheet.create({
  completedRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  statusButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    paddingVertical: isMobile ? 8 : 8,
    paddingHorizontal: isMobile ? 12 : 16,
    borderWidth: 1,
  },
  resolveText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksGreen400,
    marginLeft: 8,
  },
  startButton: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderColor: Colors.light.hereworksDarkBlue,
  },
  resolveButton: {
    backgroundColor: Colors.light.hereworksGreen500,
    borderColor: Colors.light.hereworksGreen500,
    color: Colors.light.plainBackground,
  },
  completedButton: {
    backgroundColor: Colors.light.hereworksGreen100,
    borderColor: Colors.light.hereworksGreen50,
  },
  reopenButton: {
    backgroundColor: Colors.light.hereworksBlue500,
    marginStart: 8,
    borderColor: Colors.light.hereworksBlue500,
  },
});

export default TicketStatusButtonsRow;
